import { Fragment, useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material'
import {
  ContentCopy as ContentCopyIcon,
  KeyboardArrowRight as ArrowRightIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { OrchestrationActivityDto } from '../types/Constants'

interface OrchestrationTimelineProps {
  instanceId: string
  activities: OrchestrationActivityDto[]
}

const formatTime = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '')

const PayloadBlock = ({ label, value, error = false }: { label: string; value: string; error?: boolean }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" sx={{ fontWeight: 600, color: error ? 'error.main' : 'text.secondary' }}>
      {label}
    </Typography>
    <Box
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.8rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: error ? 'error.main' : 'text.primary',
        backgroundColor: 'grey.50',
        borderRadius: 1,
        p: 1,
        mt: 0.5,
      }}
    >
      {value}
    </Box>
  </Box>
)

const OrchestrationTimeline = ({ instanceId, activities }: OrchestrationTimelineProps) => {
  const { t } = useTranslation()
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const copyInstanceId = () => {
    navigator.clipboard.writeText(instanceId).catch(() => {})
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {instanceId}
        </Typography>
        <Tooltip title={t('investigation.orchestrations.copy', 'Copy')}>
          <IconButton size="small" onClick={copyInstanceId}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 40 }} />
            <TableCell>{t('investigation.orchestrations.name', 'Name')}</TableCell>
            <TableCell>{t('investigation.orchestrations.timeScheduled', 'Time Scheduled')}</TableCell>
            <TableCell>{t('investigation.orchestrations.timeCompleted', 'Time Completed')}</TableCell>
            <TableCell>{t('investigation.orchestrations.status', 'Status')}</TableCell>
            <TableCell>{t('investigation.orchestrations.isRetry', 'Retry')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {activities.map((row, i) => {
            const failed = row.status === 'Failed'
            const expandable = !!(row.output || row.error)
            const isOpen = expandedRows.has(i)
            return (
              <Fragment key={i}>
                <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                  <TableCell sx={{ p: 0, pl: 0.5 }}>
                    {expandable && (
                      <IconButton size="small" onClick={() => toggleRow(i)}>
                        {isOpen ? <ArrowDownIcon fontSize="small" /> : <ArrowRightIcon fontSize="small" />}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: failed ? 'error.main' : undefined, fontWeight: failed ? 600 : undefined }}>
                    {row.name}
                  </TableCell>
                  <TableCell sx={{ color: failed ? 'error.main' : undefined }}>{formatTime(row.scheduledAt)}</TableCell>
                  <TableCell sx={{ color: failed ? 'error.main' : undefined }}>{formatTime(row.completedAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      color={failed ? 'error' : row.status === 'Completed' ? 'success' : 'warning'}
                      variant={failed ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{row.isRetry ? '✓' : ''}</TableCell>
                </TableRow>
                {expandable && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: isOpen ? undefined : 'unset' }}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          {row.output && (
                            <PayloadBlock label={t('investigation.orchestrations.output', 'Output')} value={row.output} />
                          )}
                          {row.error && (
                            <PayloadBlock
                              label={t('investigation.orchestrations.error', 'Error')}
                              value={row.error}
                              error
                            />
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

export default OrchestrationTimeline
